import { useCallback } from 'react'
import {
  Drawer,
  VStack,
  HStack,
  Text,
  Box,
  Heading,
  IconButton,
  Card,
  Badge,
  Separator,
} from '@chakra-ui/react'
import {
  X,
  ChevronUp,
  ChevronDown,
  FileText,
  Calendar,
  CreditCard,
  Clock,
  AlertTriangle,
  RefreshCw,
  Truck,
} from 'lucide-react'
import { useContract } from '../hooks'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import type { ContractStatus, ContractType, BillingFrequency } from '../types'
import {
  CONTRACT_STATUS_LABELS,
  CONTRACT_STATUS_COLORS,
  CONTRACT_TYPE_LABELS,
  BILLING_FREQUENCY_LABELS,
} from '../types'

interface ContractDetailDrawerProps {
  contractId: string | null
  isOpen: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  hasPrevious?: boolean
  hasNext?: boolean
}

export function ContractDetailDrawer({
  contractId,
  isOpen,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious = false,
  hasNext = false,
}: ContractDetailDrawerProps) {
  const { data: contract, isLoading, error } = useContract(contractId ?? undefined)

  const handleInteractOutside = useCallback(
    (e: { detail: { originalEvent: { target: EventTarget | null } } }) => {
      const target = e.detail.originalEvent.target as HTMLElement
      const tableRow = target.closest('[data-contract-row]') as HTMLElement
      if (tableRow) return
      onClose()
    },
    [onClose]
  )

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const formatCurrency = (value: number | null | undefined) => {
    if (!value) return '-'
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  const formatDateTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const isExpiringSoon = (endDate: string | null | undefined) => {
    if (!endDate) return false
    const end = new Date(endDate)
    const today = new Date()
    const daysUntilEnd = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    return daysUntilEnd > 0 && daysUntilEnd <= 30
  }

  const isExpired = (endDate: string | null | undefined) => {
    if (!endDate) return false
    return new Date(endDate) < new Date()
  }

  const getDaysUntilEnd = (endDate: string | null | undefined) => {
    if (!endDate) return null
    const end = new Date(endDate)
    const today = new Date()
    return Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  }

  return (
    <Drawer.Root
      open={isOpen}
      placement="end"
      size="md"
      modal={false}
      onInteractOutside={handleInteractOutside}
    >
      <Drawer.Positioner pointerEvents="none">
        <Drawer.Content maxW="600px" pointerEvents="auto">
          <Drawer.Header borderBottomWidth="1px" py={4} overflow="visible">
            <Box display="flex" alignItems="center" w="full" gap={3}>
              {/* Navigation */}
              <Box display="flex" gap={1} flexShrink={0}>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigatePrevious}
                  disabled={!hasPrevious}
                  aria-label="Précédent"
                >
                  <ChevronUp size={18} />
                </IconButton>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateNext}
                  disabled={!hasNext}
                  aria-label="Suivant"
                >
                  <ChevronDown size={18} />
                </IconButton>
              </Box>

              {/* Title */}
              <Box flex={1} minW={0}>
                {contract && (
                  <>
                    <HStack gap={2} mb={1}>
                      <Badge variant="outline" size="sm" fontFamily="mono">
                        {contract.reference}
                      </Badge>
                      <Badge
                        colorPalette={CONTRACT_STATUS_COLORS[contract.status as ContractStatus]}
                        size="sm"
                      >
                        {CONTRACT_STATUS_LABELS[contract.status as ContractStatus]}
                      </Badge>
                    </HStack>
                    <Heading
                      size="md"
                      lineHeight="1.3"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {contract.label}
                    </Heading>
                  </>
                )}
                {!contract && <Heading size="md">Détail du contrat</Heading>}
              </Box>

              {/* Close */}
              <IconButton variant="ghost" size="sm" onClick={onClose} aria-label="Fermer">
                <X size={18} />
              </IconButton>
            </Box>
          </Drawer.Header>

          <Drawer.Body p={0} display="flex" flexDirection="column" height="100%">
            {isLoading && <LoadingState message="Chargement..." />}
            {error && <ErrorState message="Contrat non trouvé" />}

            {contract && (
              <VStack align="stretch" gap={0} flex={1} height="100%" overflow="auto">
                {/* Informations principales */}
                <HStack
                  gap={4}
                  px={6}
                  pt={4}
                  pb={3}
                  bg="gray.50"
                  borderBottomWidth="1px"
                  borderColor="gray.200"
                  flexWrap="wrap"
                >
                  <HStack gap={2} flex={1} minW="140px">
                    <Box color="purple.500">
                      <FileText size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Type
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {CONTRACT_TYPE_LABELS[contract.contract_type as ContractType]}
                      </Text>
                    </VStack>
                  </HStack>

                  {contract.billing_frequency && (
                    <HStack gap={2} flex={1} minW="140px">
                      <Box color="blue.500">
                        <Calendar size={16} />
                      </Box>
                      <VStack align="flex-start" gap={0}>
                        <Text fontSize="xs" color="gray.600" fontWeight="medium">
                          Facturation
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                          {BILLING_FREQUENCY_LABELS[contract.billing_frequency as BillingFrequency]}
                        </Text>
                      </VStack>
                    </HStack>
                  )}

                  <HStack gap={2} flex={1} minW="140px">
                    <Box color="green.500">
                      <CreditCard size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Montant annuel TTC
                      </Text>
                      <Text fontSize="sm" fontWeight="bold" color="green.600">
                        {formatCurrency(contract.annual_amount_ttc)}
                      </Text>
                    </VStack>
                  </HStack>
                </HStack>

                {/* Fournisseur */}
                {contract.suppliers && (
                  <Box px={6} pt={4}>
                    <Card.Root variant="outline">
                      <Card.Header
                        py={3}
                        bg="gray.50"
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                      >
                        <HStack>
                          <Truck size={16} />
                          <Heading size="sm">Fournisseur</Heading>
                        </HStack>
                      </Card.Header>
                      <Card.Body>
                        <VStack align="stretch" gap={2}>
                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              Raison sociale
                            </Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {contract.suppliers.name}
                            </Text>
                          </HStack>
                          {contract.suppliers.code && (
                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.600">
                                Code
                              </Text>
                              <Text fontSize="sm" fontWeight="medium" fontFamily="mono">
                                {contract.suppliers.code}
                              </Text>
                            </HStack>
                          )}
                          {contract.suppliers.email && (
                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.600">
                                Email
                              </Text>
                              <Text fontSize="sm">{contract.suppliers.email}</Text>
                            </HStack>
                          )}
                          {contract.suppliers.phone && (
                            <HStack justify="space-between">
                              <Text fontSize="sm" color="gray.600">
                                Téléphone
                              </Text>
                              <Text fontSize="sm">{contract.suppliers.phone}</Text>
                            </HStack>
                          )}
                        </VStack>
                      </Card.Body>
                    </Card.Root>
                  </Box>
                )}

                {/* Période et échéances */}
                <Box px={6} pt={4}>
                  <Card.Root variant="outline">
                    <Card.Header py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
                      <HStack justify="space-between">
                        <HStack>
                          <Calendar size={16} />
                          <Heading size="sm">Période</Heading>
                        </HStack>
                        {isExpiringSoon(contract.end_date) && (
                          <Badge colorPalette="orange" size="sm">
                            <AlertTriangle size={12} />
                            <Text ml={1}>Expire dans {getDaysUntilEnd(contract.end_date)} jours</Text>
                          </Badge>
                        )}
                        {isExpired(contract.end_date) && contract.status === 'ACTIVE' && (
                          <Badge colorPalette="red" size="sm">
                            <AlertTriangle size={12} />
                            <Text ml={1}>Expiré</Text>
                          </Badge>
                        )}
                      </HStack>
                    </Card.Header>
                    <Card.Body>
                      <VStack align="stretch" gap={3}>
                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">
                            Date de début
                          </Text>
                          <Text fontSize="sm" fontWeight="medium">
                            {formatDate(contract.start_date)}
                          </Text>
                        </HStack>
                        {contract.end_date && (
                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              Date de fin
                            </Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {formatDate(contract.end_date)}
                            </Text>
                          </HStack>
                        )}

                        <Separator />

                        <HStack gap={2}>
                          <RefreshCw
                            size={14}
                            color={
                              contract.is_tacit_renewal
                                ? 'var(--chakra-colors-green-500)'
                                : 'var(--chakra-colors-gray-400)'
                            }
                          />
                          <Text fontSize="sm">
                            {contract.is_tacit_renewal
                              ? 'Tacite reconduction activée'
                              : 'Pas de tacite reconduction'}
                          </Text>
                        </HStack>

                        {contract.renewal_notice_days && (
                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              Préavis de résiliation
                            </Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {contract.renewal_notice_days} jours
                            </Text>
                          </HStack>
                        )}
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                </Box>

                {/* Montants */}
                <Box px={6} pt={4}>
                  <Card.Root variant="outline">
                    <Card.Header py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
                      <HStack>
                        <CreditCard size={16} />
                        <Heading size="sm">Montants</Heading>
                      </HStack>
                    </Card.Header>
                    <Card.Body>
                      <VStack align="stretch" gap={3}>
                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">
                            Montant annuel HT
                          </Text>
                          <Text fontSize="sm" fontWeight="medium" fontFamily="mono">
                            {formatCurrency(contract.annual_amount_ht)}
                          </Text>
                        </HStack>
                        {contract.vat_rate && (
                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              Taux de TVA
                            </Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {contract.vat_rate}%
                            </Text>
                          </HStack>
                        )}
                        <Separator />
                        <HStack justify="space-between">
                          <Text fontSize="sm" fontWeight="medium">
                            Montant annuel TTC
                          </Text>
                          <Text fontSize="md" fontWeight="bold" fontFamily="mono" color="green.600">
                            {formatCurrency(contract.annual_amount_ttc)}
                          </Text>
                        </HStack>
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                </Box>

                {/* Description */}
                {contract.description && (
                  <Box px={6} pt={4}>
                    <Card.Root variant="outline">
                      <Card.Header
                        py={3}
                        bg="gray.50"
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                      >
                        <HStack>
                          <FileText size={16} />
                          <Heading size="sm">Description</Heading>
                        </HStack>
                      </Card.Header>
                      <Card.Body>
                        <Text fontSize="sm" whiteSpace="pre-wrap">
                          {contract.description}
                        </Text>
                      </Card.Body>
                    </Card.Root>
                  </Box>
                )}

                {/* Conditions de paiement */}
                {contract.payment_terms && (
                  <Box px={6} pt={4}>
                    <Card.Root variant="outline">
                      <Card.Header
                        py={3}
                        bg="gray.50"
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                      >
                        <HStack>
                          <CreditCard size={16} />
                          <Heading size="sm">Conditions de paiement</Heading>
                        </HStack>
                      </Card.Header>
                      <Card.Body>
                        <Text fontSize="sm">{contract.payment_terms}</Text>
                      </Card.Body>
                    </Card.Root>
                  </Box>
                )}

                {/* Notes */}
                {contract.notes && (
                  <Box px={6} pt={4}>
                    <Card.Root variant="outline">
                      <Card.Header
                        py={3}
                        bg="gray.50"
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                      >
                        <HStack>
                          <FileText size={16} />
                          <Heading size="sm">Notes</Heading>
                        </HStack>
                      </Card.Header>
                      <Card.Body>
                        <Text fontSize="sm" whiteSpace="pre-wrap">
                          {contract.notes}
                        </Text>
                      </Card.Body>
                    </Card.Root>
                  </Box>
                )}

                {/* Historique */}
                <Box px={6} pt={4} pb={6}>
                  <Card.Root variant="outline">
                    <Card.Header py={3}>
                      <Heading size="sm">Historique</Heading>
                    </Card.Header>
                    <Card.Body>
                      <VStack align="stretch" gap={3}>
                        <HStack gap={3}>
                          <Box color="gray.400">
                            <Clock size={16} />
                          </Box>
                          <VStack align="flex-start" gap={0}>
                            <Text fontSize="sm" fontWeight="medium">
                              Créé le
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              {formatDateTime(contract.created_at)}
                            </Text>
                          </VStack>
                        </HStack>

                        {contract.updated_at && contract.updated_at !== contract.created_at && (
                          <HStack gap={3}>
                            <Box color="blue.500">
                              <Clock size={16} />
                            </Box>
                            <VStack align="flex-start" gap={0}>
                              <Text fontSize="sm" fontWeight="medium">
                                Modifié le
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {formatDateTime(contract.updated_at)}
                              </Text>
                            </VStack>
                          </HStack>
                        )}
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                </Box>
              </VStack>
            )}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  )
}
